import React from "react";

interface LogoProps {
  className?: string;
  variant?: "full" | "icon";
  size?: "sm" | "md" | "lg" | "xl";
}

export default function ClearCurveFinal({ className = "", variant = "full", size = "md" }: LogoProps) {
  // Dimensions mapping
  const dimensions = {
    sm: { height: 32 },
    md: { height: 48 },
    lg: { height: 64 },
    xl: { height: 80 }, // Header size usually around md/lg
  };
  
  const dim = dimensions[size] || dimensions.md;
  
  // VIEWBOX CALCULATIONS
  // Icon is roughly 45x80. 
  // Text text length is approx 350-400px at this font size.
  const fullViewBox = "0 0 450 100";
  const iconViewBox = "0 0 70 100";

  return (
    <div className={`flex items-center ${className}`} style={{ height: dim.height }}>
      <svg
        viewBox={variant === "full" ? fullViewBox : iconViewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        style={{ height: "100%" }}
        className="overflow-visible"
      >
        <style>{`
          @keyframes drawLogoLine {
            0% { stroke-dashoffset: 200; opacity: 0; }
            30% { opacity: 1; }
            100% { stroke-dashoffset: 0; opacity: 1; }
          }

          @keyframes candleGrowTransform {
            0%, 100% { transform: scaleY(1); }
            50% { transform: scaleY(1.55); }
          }

          @keyframes candleShrinkTransform {
            0%, 100% { transform: scaleY(1); }
            50% { transform: scaleY(0.55); }
          }

          .anim-path {
            stroke-dasharray: 200;
            stroke-dashoffset: 200;
            animation: drawLogoLine 2.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          .anim-candle-red,
          .anim-candle-green {
            transform-box: fill-box;
            transform-origin: center bottom;
            will-change: transform;
          }

          .anim-candle-red {
            animation: candleGrowTransform 3s ease-in-out infinite alternate;
          }

          .anim-candle-green {
            animation: candleShrinkTransform 2.5s ease-in-out infinite alternate;
          }

          @media (prefers-reduced-motion: reduce) {
            .anim-path,
            .anim-candle-red,
            .anim-candle-green {
              animation: none;
              stroke-dashoffset: 0;
              transform: none;
            }
          }
        `}</style>

        {/* ICON GROUP */}
        <g transform="translate(5, 5)">
            {/* The 'C' Curve - White */}
            <path 
                d="M 55 10 L 15 50 L 55 90" 
                fill="none" 
                stroke="#ffffff" 
                strokeWidth="8" 
                strokeLinecap="square"
                className="anim-path"
            />
            
            {/* Candles Group - Tucked inside */}
            <g transform="translate(45, 20)">
                {/* Red Candle */}
                <rect x="0" y="25" width="8" height="25" fill="#ef4444" className="anim-candle-red" rx="1" />
                <line x1="4" y1="10" x2="4" y2="60" stroke="#ef4444" strokeWidth="2" />
                
                {/* Green Candle */}
                <rect x="16" y="10" width="8" height="40" fill="#10b981" className="anim-candle-green" rx="1" />
                <line x1="20" y1="0" x2="20" y2="60" stroke="#10b981" strokeWidth="2" />
            </g>
        </g>

        {/* TEXT GROUP - Only for full variant */}
        {variant === "full" && (
            // Tight spacing: x starting at 80 (Icon ends approx 70, gap 10 px)
            <>
              {/* Brand exception: Inter is the logotype choice for the ClearCurve wordmark. Do not replace with var(--font-body). */}
              <text x="75" y="70" fontFamily="'Inter', sans-serif" fontSize="58" fill="#f8fafc" letterSpacing="-2px">
                {/* Tight Lockup: Bold 'Clear' + Thin Green 'Curve' */}
                <tspan fontWeight="800">Clear</tspan>
                <tspan fontWeight="300" fill="#10b981">Curve</tspan>
            </text>
            </>
        )}
      </svg>
    </div>
  );
}
